import React, { ReactNode, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import Button from "components/Button";
import { useSentryInit } from "./hooks/useSentryInit";
import { useSentryUserSession } from "./hooks/useSentryUserSession";
import { trackException } from "hooks/useGATracking";
import CrashReportModal from "./CrashReportModal";

interface SentryProviderProps {
  children: ReactNode;
}

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  resetError: () => void;
}

function ErrorFallback({ error, componentStack, resetError }: ErrorFallbackProps) {
  const [eventId, setEventId] = useState<string | null>(null);
  const [isReportModalOpen, setReportModalOpen] = useState(false);

  useEffect(() => {
    const id = Sentry.captureException(error, {
      contexts: {
        react: { componentStack }
      }
    });
    setEventId(id);

    // Also send to GA4 so it shows up in BigQuery
    const errorMessage = error instanceof Error ? error.message : String(error);
    trackException(`React crash: ${errorMessage}`, true);
  }, [error, componentStack]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full bg-card shadow-lg rounded-2xl p-6">
        <h2 className="text-2xl font-semibold text-red-600 mb-4">Something went wrong</h2>
        <p className="text-text text-base mb-6 leading-relaxed">
          An unexpected error occurred. We&apos;ve been notified and are looking into it. Please try
          refreshing the page.
        </p>
        <div className="flex gap-3">
          <Button onClick={resetError} className="flex-1" variant="contained">
            Try Again
          </Button>
          <Button
            onClick={() => setReportModalOpen(true)}
            className="flex-1"
            variant="outline"
            disabled={!eventId}>
            Report Issue
          </Button>
        </div>
      </div>
      <CrashReportModal
        isOpen={isReportModalOpen}
        closeModal={() => setReportModalOpen(false)}
        source="crash"
        eventId={eventId || undefined}
        errorMessage={error instanceof Error ? error.message : String(error)}
      />
    </div>
  );
}

export function SentryProvider({ children }: SentryProviderProps) {
  useSentryInit();
  useSentryUserSession();

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, resetError }) => (
        <ErrorFallback error={error} componentStack={componentStack} resetError={resetError} />
      )}>
      <Sentry.Profiler name="App" updateProps={{}}>
        {children}
      </Sentry.Profiler>
    </Sentry.ErrorBoundary>
  );
}
